import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Mail,
  MessageSquare,
  ShieldCheck,
  Phone,
  CheckCircle2,
  Loader2,
  Copy,
  Download,
  Plus,
  X,
  AlertTriangle,
} from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import AuthCard from "@/components/jea/AuthCard";
import JeaButton from "@/components/jea/JeaButton";
import JeaInput from "@/components/jea/JeaInput";
import { apiClient, extractApiErrorMeta, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

/** Phase 10E — Dedicated 2FA selection screen (MANDATORY in onboarding).
 *
 * Three option cards: Email, SMS, Authenticator App. Each is either:
 *  - Enabled → "Select" starts enrollment for that method
 *  - Disabled → shows the missing-contact tooltip + inline "Add email/phone"
 *    form that verifies the new value and unlocks the card
 *
 * Voice is NOT shown here — it's only a conditional fallback on the login
 * chip row when SMS fails (Phase 10E spec).
 */

const CARDS = [
  {
    key: "email",
    label: "Email",
    icon: Mail,
    description: "Receive a 6-digit code via email at the address you verified.",
    needs: "email",
  },
  {
    key: "sms",
    label: "Text Message (SMS)",
    icon: MessageSquare,
    description: "Receive a 6-digit code via SMS. We'll offer a voice call as a backup if SMS fails.",
    needs: "phone",
  },
  {
    key: "totp",
    label: "Authenticator App",
    icon: ShieldCheck,
    description: "Use Google Authenticator, Authy, 1Password, or similar. Works without network coverage.",
    needs: null,
    tagline: "Recommended for highest security",
  },
];

/** Format E.164 US phone as "+1 (XXX) XXX-XXXX" if possible; else return as-is. */
const formatPhoneForDisplay = (p) => {
  if (!p) return "";
  const m = String(p).match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : p;
};

const sixInput = (value, onChange, testPrefix) => {
  const digits = value.padEnd(6, " ").split("").map((c) => (c === " " ? "" : c));
  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          inputMode="numeric"
          maxLength={1}
          value={digits[i]}
          onChange={(e) => {
            const d = e.target.value.replace(/\D/g, "").slice(-1);
            const next = [...digits];
            next[i] = d;
            onChange(next.join("").replace(/\s/g, ""));
            if (d && i < 5) {
              const el = document.querySelector(`[data-testid="${testPrefix}-${i + 1}"]`);
              if (el) el.focus();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !digits[i] && i > 0) {
              const el = document.querySelector(`[data-testid="${testPrefix}-${i - 1}"]`);
              if (el) el.focus();
            }
          }}
          data-testid={`${testPrefix}-${i}`}
          className={cn(
            "w-11 h-14 rounded-md text-center text-xl font-bold text-white",
            "bg-bg-tertiary border border-border-default outline-none",
            "focus:border-success focus:shadow-[0_0_0_4px_rgba(46,204,113,0.30)]",
            "transition-all duration-150",
          )}
        />
      ))}
    </div>
  );
};

const TwoFactorSetupPage = () => {
  const nav = useNavigate();
  const { user, refreshMe } = useAuth();
  const [contact, setContact] = useState(null);
  const [enrolled, setEnrolled] = useState([]);
  const [primary, setPrimary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // {kind, step}
  const [backupCodes, setBackupCodes] = useState(null);
  const [primaryPicked, setPrimaryPicked] = useState(false);

  const load = async () => {
    try {
      const [{ data: c }, { data: s }] = await Promise.all([
        apiClient.get("/account/contact"),
        apiClient.get("/account/2fa/status"),
      ]);
      setContact(c);
      setEnrolled(s.enrolled_channels || []);
      setPrimary(s.primary_channel || null);
      if (s.primary_channel) setPrimaryPicked(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const isCardEnabled = (card) => {
    if (card.needs === "email") return !!(contact?.email && contact?.email_verified);
    if (card.needs === "phone") return !!(contact?.phone_number && contact?.phone_verified);
    return true;
  };

  const finish = async () => {
    await refreshMe();
    nav("/dashboard", { replace: true });
  };

  if (loading) {
    return (
      <AuthCard>
        <div className="flex items-center justify-center py-8 text-text-secondary">
          <Loader2 className="animate-spin w-5 h-5 mr-2" /> Loading…
        </div>
      </AuthCard>
    );
  }

  // Backup codes display after any first enrollment
  if (backupCodes) {
    const text = backupCodes.join("\n");
    return (
      <AuthCard>
        <div className="space-y-5" data-testid="twofactor-backup-codes-panel">
          <div className="text-center space-y-1">
            <h1 className="text-xl font-bold text-white tracking-tight">
              Save your backup codes
            </h1>
            <p className="text-[13px] text-text-secondary">
              Store these somewhere safe. Each code can be used ONCE if you lose
              access to your primary 2FA method.
            </p>
          </div>
          <pre
            className="rounded-md border border-border-default bg-bg-tertiary px-4 py-3 text-[13px] font-mono text-white leading-relaxed"
            data-testid="backup-codes-list"
          >
            {text}
          </pre>
          <div className="flex gap-2">
            <JeaButton
              type="button"
              variant="outline-neutral"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(text);
                toast.success("Backup codes copied to clipboard.");
              }}
              data-testid="backup-copy-btn"
            >
              <Copy size={14} /> Copy
            </JeaButton>
            <JeaButton
              type="button"
              variant="outline-neutral"
              size="sm"
              onClick={() => {
                const blob = new Blob([text], { type: "text/plain" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = "jea-backup-codes.txt";
                a.click();
                URL.revokeObjectURL(a.href);
              }}
              data-testid="backup-download-btn"
            >
              <Download size={14} /> Download
            </JeaButton>
          </div>
          <JeaButton
            type="button"
            size="lg"
            fullWidth
            onClick={() => {
              setBackupCodes(null);
              toast.success("Backup codes saved.");
            }}
            data-testid="backup-done-btn"
          >
            I've saved my codes
          </JeaButton>
        </div>
      </AuthCard>
    );
  }

  if (active) {
    return (
      <ActiveEnrollment
        kind={active.kind}
        contact={contact}
        onCancel={() => setActive(null)}
        onEnrolled={async (result) => {
          setActive(null);
          await load();
          if (result?.backup_codes?.length) {
            setBackupCodes(result.backup_codes);
          }
          toast.success(`${active.kind.toUpperCase()} enrolled.`);
        }}
        onContactUpdated={async () => {
          await load();
        }}
      />
    );
  }

  const hasEnrollment = enrolled.length > 0;

  return (
    <AuthCard className="max-w-4xl">
      <div className="space-y-6" data-testid="twofactor-setup-page">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-white tracking-tight">
            {hasEnrollment ? "Add a backup 2FA method" : "Choose Your 2FA Method"}
          </h1>
          <p className="text-[15px] text-text-secondary max-w-xl mx-auto">
            {hasEnrollment
              ? "You can enroll additional methods as fallbacks. Primary stays as the first one you set up (change anytime in Settings)."
              : "Select a primary method to secure your account. You can add additional methods later from Settings."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4" data-testid="twofactor-cards">
          {CARDS.map((card) => {
            const Icon = card.icon;
            const enabled = isCardEnabled(card);
            const already = enrolled.includes(card.key);
            // Build the subtitle shown directly under the card title.
            let subtitle = null;
            if (card.key === "email" && contact?.email) {
              subtitle = (
                <div className="font-mono text-[12px] text-text-secondary truncate" title={contact.email}>
                  {contact.email}
                </div>
              );
            } else if (card.key === "sms" && contact?.phone_number) {
              subtitle = (
                <div className="font-mono text-[12px] text-text-secondary">
                  {formatPhoneForDisplay(contact.phone_number)}
                </div>
              );
            } else if (card.key === "totp") {
              subtitle = (
                <div className="text-[11px] text-success font-semibold uppercase tracking-[0.08em]">
                  {card.tagline}
                </div>
              );
            }
            return (
              <div
                key={card.key}
                data-testid={`twofactor-card-${card.key}`}
                data-enabled={enabled}
                className={cn(
                  "rounded-xl border bg-bg-primary p-7 flex flex-col gap-3 transition-all",
                  "min-h-[280px]",
                  enabled
                    ? "border-border-default hover:border-success hover:border-2 hover:shadow-[0_0_0_4px_rgba(46,204,113,0.12)]"
                    : "border-border-default opacity-70",
                  already && "border-success border-2 bg-success/5",
                )}
                title={!enabled ? `Add and verify a ${card.needs} to use this method` : undefined}
              >
                <div className="flex items-start justify-between">
                  <Icon
                    size={40}
                    className={cn(
                      enabled ? "text-success" : "text-text-muted",
                      already && "text-success",
                    )}
                  />
                  {already && <CheckCircle2 size={20} className="text-success" />}
                </div>
                <div className="space-y-1">
                  <div className="text-[18px] font-bold text-white leading-tight">
                    {card.label}
                  </div>
                  {subtitle}
                </div>
                <div className="text-[13px] text-text-secondary leading-relaxed">
                  {card.description}
                </div>
                <div className="flex-1" />
                {!enabled ? (
                  <AddContactInline
                    kind={card.needs}
                    onContactUpdated={async () => {
                      await load();
                      toast.success(
                        card.needs === "email"
                          ? "Email verified — you can now choose this method."
                          : "Phone verified — you can now choose this method.",
                      );
                    }}
                  />
                ) : (
                  <JeaButton
                    type="button"
                    size="md"
                    variant={already ? "outline-neutral" : "primary"}
                    onClick={() => setActive({ kind: card.key })}
                    disabled={already}
                    fullWidth
                    data-testid={`twofactor-select-${card.key}`}
                  >
                    {already
                      ? "Enrolled"
                      : card.key === "email"
                        ? "Select Email"
                        : card.key === "sms"
                          ? "Select SMS"
                          : "Select Authenticator"}
                  </JeaButton>
                )}
              </div>
            );
          })}
        </div>

        <p
          className="text-[12px] text-text-muted text-center mt-4 max-w-2xl mx-auto"
          data-testid="twofactor-helper-text"
        >
          Your selection will be set as your primary 2FA method. You can enroll
          additional backup methods from Settings after setup.
        </p>

        {hasEnrollment && (
          <div className="flex items-center justify-between pt-4 border-t border-border-default">
            <div className="text-[13px] text-text-secondary">
              Primary method: <span className="font-semibold text-white">{(primary || enrolled[0] || "").toUpperCase()}</span>
            </div>
            <JeaButton
              type="button"
              size="md"
              onClick={finish}
              data-testid="twofactor-finish-btn"
            >
              Continue to dashboard
            </JeaButton>
          </div>
        )}
      </div>
    </AuthCard>
  );
};

/* ========================= AddContactInline ========================= */
const AddContactInline = ({ kind, onContactUpdated }) => {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("input"); // input | verify
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const start = async () => {
    setErr("");
    if (!val) { setErr("Required."); return; }
    if (kind === "phone" && !isValidPhoneNumber(val)) {
      setErr("Enter a valid phone number including country code.");
      return;
    }
    setBusy(true);
    try {
      if (kind === "email") {
        await apiClient.post("/account/contact/update-email", { email: val });
      } else {
        await apiClient.post("/account/contact/update-phone", { phone: val });
      }
      setStep("verify");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setErr("");
    if (code.length !== 6) { setErr("Enter the 6-digit code."); return; }
    setBusy(true);
    try {
      if (kind === "email") {
        await apiClient.post("/account/contact/confirm-email", { code });
      } else {
        await apiClient.post("/account/contact/confirm-phone", { code });
      }
      setOpen(false);
      setVal("");
      setCode("");
      setStep("input");
      await onContactUpdated();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <JeaButton
        type="button"
        variant="outline-neutral"
        size="sm"
        fullWidth
        onClick={() => setOpen(true)}
        data-testid={`twofactor-add-${kind}-btn`}
      >
        <Plus size={14} /> Add {kind === "email" ? "email" : "phone"}
      </JeaButton>
    );
  }

  return (
    <div
      className="rounded-md border border-border-default bg-bg-tertiary/60 p-2 space-y-2"
      data-testid={`twofactor-add-${kind}-form`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-text-muted font-medium">
          Add {kind}
        </div>
        <button
          type="button"
          onClick={() => {
            setOpen(false); setStep("input"); setErr(""); setVal(""); setCode("");
          }}
          className="text-text-secondary hover:text-white"
          data-testid={`twofactor-add-${kind}-cancel`}
        >
          <X size={14} />
        </button>
      </div>
      {step === "input" ? (
        <>
          {kind === "email" ? (
            <JeaInput
              type="email"
              placeholder="you@example.com"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              data-testid={`twofactor-add-${kind}-value`}
            />
          ) : (
            <PhoneInput
              international
              defaultCountry="US"
              value={val}
              onChange={(v) => setVal(v || "")}
              placeholder="+1 555 555 0123"
              className="jea-phone-input"
              data-testid={`twofactor-add-${kind}-value`}
            />
          )}
          {err && (
            <div
              className="text-[12px] text-danger"
              data-testid={`twofactor-add-${kind}-error`}
            >
              {err}
            </div>
          )}
          <JeaButton
            type="button"
            size="sm"
            fullWidth
            loading={busy}
            onClick={start}
            data-testid={`twofactor-add-${kind}-send`}
          >
            Send verification
          </JeaButton>
        </>
      ) : (
        <>
          <div className="text-[12px] text-text-secondary">
            Enter the 6-digit code sent to {val}.
          </div>
          {sixInput(code, setCode, `twofactor-add-${kind}-digit`)}
          {err && (
            <div
              className="text-[12px] text-danger"
              data-testid={`twofactor-add-${kind}-error`}
            >
              {err}
            </div>
          )}
          <JeaButton
            type="button"
            size="sm"
            fullWidth
            loading={busy}
            onClick={confirm}
            data-testid={`twofactor-add-${kind}-confirm`}
          >
            Verify
          </JeaButton>
        </>
      )}
    </div>
  );
};

/* ========================= ActiveEnrollment ========================= */
const ActiveEnrollment = ({ kind, contact, onCancel, onEnrolled, onContactUpdated }) => {
  const [stage, setStage] = useState(kind === "totp" ? "totp-start" : "sent");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [makePrimary, setMakePrimary] = useState(true);
  // TOTP state
  const [factorSid, setFactorSid] = useState("");
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");

  const target = kind === "email" ? contact?.email : contact?.phone_number;
  const isPhone = kind === "sms" || kind === "voice";

  useEffect(() => {
    const run = async () => {
      try {
        if (kind === "totp") {
          const { data } = await apiClient.post("/account/2fa/enroll/totp-start");
          setFactorSid(data.factor_sid);
          setQr(data.qr_data_url || "");
          setSecret(data.secret || "");
          setStage("totp-ready");
        } else if (isPhone) {
          // Persist pending phone first, then start Twilio Verify
          await apiClient.post("/account/2fa/enroll/phone", { phone: contact.phone_number });
          await apiClient.post("/account/2fa/enroll/start", {
            channel: "sms",
            phone: contact.phone_number,
          });
          setStage("sent");
        } else {
          // email
          await apiClient.post("/account/2fa/enroll/start", {
            channel: "email",
            email: contact.email,
          });
          setStage("sent");
        }
      } catch (e) {
        setErr(formatApiError(e));
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = async () => {
    setErr("");
    setBusy(true);
    try {
      if (kind === "totp") {
        const { data } = await apiClient.post("/account/2fa/enroll/totp-confirm", {
          factor_sid: factorSid,
          code,
          make_primary: makePrimary,
        });
        onEnrolled(data);
      } else {
        const { data } = await apiClient.post("/account/2fa/enroll/confirm", {
          channel: kind === "sms" ? "sms" : "email",
          code,
          make_primary: makePrimary,
        });
        onEnrolled(data);
      }
    } catch (e) {
      const meta = extractApiErrorMeta(e);
      setErr(meta.message || formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard>
      <div className="space-y-5" data-testid={`twofactor-active-${kind}`}>
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-white tracking-tight">
            Enroll {kind === "totp" ? "authenticator app" : kind === "sms" ? "SMS" : "email"}
          </h1>
          <button
            type="button"
            onClick={onCancel}
            className="text-text-secondary hover:text-white"
            data-testid="twofactor-active-cancel"
          >
            <X size={16} />
          </button>
        </div>

        {kind === "totp" && stage === "totp-start" && (
          <div className="flex items-center justify-center py-6 text-text-secondary">
            <Loader2 className="animate-spin w-5 h-5 mr-2" /> Preparing enrollment…
          </div>
        )}
        {kind === "totp" && stage === "totp-ready" && (
          <div className="space-y-3">
            <div className="text-[13px] text-text-secondary">
              Scan this QR code in your authenticator app, then enter the first 6-digit code it displays.
            </div>
            {qr ? (
              <img
                src={qr}
                alt="TOTP QR code"
                className="w-40 h-40 mx-auto bg-white rounded-md p-2"
                data-testid="twofactor-totp-qr"
              />
            ) : (
              <div className="text-[12px] text-text-muted">QR unavailable — enter the secret manually.</div>
            )}
            {secret && (
              <div
                className="text-[12px] font-mono text-text-secondary bg-bg-tertiary px-3 py-2 rounded-md break-all"
                data-testid="twofactor-totp-secret"
              >
                {secret}
              </div>
            )}
            {sixInput(code, setCode, "twofactor-totp-digit")}
          </div>
        )}

        {(kind === "email" || isPhone) && (
          <div className="space-y-3">
            <div className="text-[13px] text-text-secondary">
              Enter the 6-digit code sent to {target}.
            </div>
            {sixInput(code, setCode, `twofactor-${kind}-digit`)}
          </div>
        )}

        <label className="flex items-center gap-2 text-[13px] text-text-secondary">
          <input
            type="checkbox"
            checked={makePrimary}
            onChange={(e) => setMakePrimary(e.target.checked)}
            className="accent-success"
            data-testid="twofactor-make-primary"
          />
          Make this my primary 2FA method
        </label>

        {err && (
          <div
            className="text-[13px] text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2"
            data-testid="twofactor-active-error"
          >
            {err}
          </div>
        )}

        <JeaButton
          type="button"
          size="lg"
          fullWidth
          loading={busy}
          onClick={confirm}
          disabled={code.length !== 6}
          data-testid="twofactor-active-confirm"
        >
          Verify and enroll
        </JeaButton>
      </div>
    </AuthCard>
  );
};

export default TwoFactorSetupPage;
